import { useRef } from 'react';

type TPagination = {
  page: number;
  totalNoPages: number;
  perPage: number;
};

export const calculatePagination = (
  { page, totalNoPages }: TPagination,
  currentPage: number,
  onPageDiff: (page: number) => void,
) => {
  const pageIndex = page - 1;
  const groupSize = 4;

  // if page is larger than current page, then step should be 1, else -1
  const pages = Array.from({ length: totalNoPages }, (_, i) => i);
  let group = pages.slice(pageIndex, groupSize + pageIndex);
  const endGroup = pages.slice(totalNoPages - groupSize, totalNoPages);
  const shouldResetEndGroup = page === endGroup[0] + 1;

  if (page <= totalNoPages) {
    // if page is less than perPage from start, then group should be from 0 to perPage
    if (page < groupSize) {
      group = pages.slice(0, groupSize);
    } else if (page > totalNoPages - groupSize && !shouldResetEndGroup) {
      // if page is larger than totalPages - groupSize, then group should be from totalPages - groupSize to totalPages
      group = pages.slice(totalNoPages - groupSize, totalNoPages);
    } else {
      // group should be padded with 1 on each side
      group = [pageIndex - 1, pageIndex, pageIndex + 1];
    }

    if (currentPage !== page) {
      onPageDiff(page);
    }
  }

  return {
    group,
    pageIndex,
    pages,
    showStartEllipsis: !group?.includes(1) && totalNoPages > 1,
    showEndEllipsis: !group?.includes(totalNoPages - 1) && totalNoPages > 1,
  };
};

export const usePagination = (pagination: TPagination) => {
  const currentPage = useRef<number>(pagination.page);

  return calculatePagination(pagination, currentPage.current, (newPage) => {
    currentPage.current = newPage;
  });
};
